import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatErr } from "@/lib/api";
import { Plus, Save, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

const EMPTY = {
  id: null, order: 0, image: "",
  kicker: "New Chapter", title_line1: "Elegance", title_line2: "Redefined",
  subtitle: "Contemporary Indian Couture",
  cta_text: "Explore Collection", cta_link: "/collections",
  is_active: true,
};

export default function AdminHero() {
  const { user } = useAuth();
  const [slides, setSlides] = useState([]);
  const [media, setMedia] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([api.get("/admin/hero-slides"), api.get("/media")]);
      setSlides(s.data); setMedia(m.data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/hero" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux min-h-[60vh]">
      <p className="overline text-muted-foreground">Restricted</p>
      <h1 className="mt-4 font-serif text-4xl">Admin access required</h1>
    </div>
  );

  const openNew = () => setEditing({ ...EMPTY, order: slides.length });
  const openEdit = (s) => setEditing({ ...EMPTY, ...s });
  const close = () => setEditing(null);
  const set = (k, v) => setEditing({ ...editing, [k]: v });

  const save = async () => {
    if (!editing) return;
    if (!editing.title_line1) return toast.error("First title line is required");
    if (!editing.image) return toast.error("An image is required");
    setSaving(true);
    // if user selected a media library URL, keep it relative — Hero will absolutize
    const payload = {
      order: Number(editing.order) || 0,
      image: editing.image,
      kicker: editing.kicker || "",
      title_line1: editing.title_line1,
      title_line2: editing.title_line2 || "",
      subtitle: editing.subtitle || "",
      cta_text: editing.cta_text || "Explore",
      cta_link: editing.cta_link || "/collections",
      is_active: editing.is_active !== false,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/hero-slides/${editing.id}`, payload);
        toast.success("Slide updated");
      } else {
        await api.post("/admin/hero-slides", payload);
        toast.success("Slide created");
      }
      close();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing?.id) return;
    if (!confirm("Delete this slide?")) return;
    try { await api.delete(`/admin/hero-slides/${editing.id}`); toast("Slide deleted"); close(); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= slides.length) return;
    const a = slides[idx], b = slides[j];
    try {
      await Promise.all([
        api.put(`/admin/hero-slides/${a.id}`, { ...a, order: j }),
        api.put(`/admin/hero-slides/${b.id}`, { ...b, order: idx }),
      ]);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleActive = async (s) => {
    try { await api.put(`/admin/hero-slides/${s.id}`, { ...s, is_active: !s.is_active }); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-hero-page">
      <div className="container-lux">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">Atelier · Admin</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Hero Slides</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">Choreograph the three (or more) editorial slides that open the storefront. Reorder, toggle live, edit copy and swap imagery from the Media Library.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/media" className="lux-btn-outline py-3 px-5 text-[10px]">Media Library</Link>
            <button onClick={openNew} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="new-slide-btn"><Plus size={13} strokeWidth={1} /> New Slide</button>
          </div>
        </div>

        {loading ? (
          <p className="mt-14 text-sm text-muted-foreground">Loading slides…</p>
        ) : slides.length === 0 ? (
          <div className="mt-14 border border-dashed border-border p-16 text-center text-muted-foreground">
            <p className="font-serif text-2xl">No slides yet</p>
            <p className="mt-4 text-sm">Create the first hero slide to open your storefront.</p>
          </div>
        ) : (
          <ul className="mt-14 space-y-6">
            {slides.map((s, idx) => (
              <li key={s.id} className="grid grid-cols-[240px_1fr_auto] gap-8 items-center border border-border p-6" data-testid={`slide-${s.id}`}>
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  {s.image && <img src={abs(s.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <span className="overline text-muted-foreground">Slide {idx + 1}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${s.is_active ? "border-ink text-ink" : "border-border text-muted-foreground"}`}>{s.is_active ? "Live" : "Hidden"}</span>
                  </div>
                  <p className="mt-3 overline text-muted-foreground">{s.kicker}</p>
                  <p className="font-serif text-3xl md:text-4xl tracking-tighter font-light mt-1">
                    {s.title_line1}{s.title_line2 && <> · <em className="not-italic">{s.title_line2}</em></>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">{s.subtitle}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{s.cta_text} → {s.cta_link}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-2 border border-border disabled:opacity-30 hover:bg-muted" data-testid={`up-${s.id}`}><ArrowUp size={13} strokeWidth={1} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === slides.length - 1} className="p-2 border border-border disabled:opacity-30 hover:bg-muted" data-testid={`down-${s.id}`}><ArrowDown size={13} strokeWidth={1} /></button>
                  </div>
                  <button onClick={() => toggleActive(s)} className="p-2 border border-border hover:bg-muted" data-testid={`toggle-${s.id}`}>
                    {s.is_active ? <Eye size={13} strokeWidth={1} /> : <EyeOff size={13} strokeWidth={1} />}
                  </button>
                  <button onClick={() => openEdit(s)} className="lux-btn-outline py-2 px-4 text-[10px]" data-testid={`edit-slide-${s.id}`}>Edit</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-ivory border border-border rounded-none p-0">
          <div className="p-8 border-b border-border sticky top-0 bg-ivory z-10 flex items-center justify-between">
            <div>
              <p className="overline text-muted-foreground">{editing?.id ? "Editing Slide" : "New Slide"}</p>
              <DialogHeader><DialogTitle className="font-serif text-3xl md:text-4xl tracking-tighter font-light mt-2">{editing?.title_line1 || "Untitled Slide"}</DialogTitle></DialogHeader>
            </div>
            <div className="flex gap-2">
              {editing?.id && <button onClick={del} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="delete-slide"><Trash2 size={12} strokeWidth={1} /> Delete</button>}
              <button onClick={close} className="lux-btn-outline py-3 px-5 text-[10px]">Close</button>
              <button onClick={save} disabled={saving} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="save-slide"><Save size={12} strokeWidth={1} /> {saving ? "Saving…" : "Save"}</button>
            </div>
          </div>

          {editing && (
          <div className="p-8 grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field label="Kicker · overline text" value={editing.kicker} onChange={(e) => set("kicker", e.target.value)} placeholder="Autumn Winter 2026" testid="s-kicker" />
              <Field label="Title · line 1" value={editing.title_line1} onChange={(e) => set("title_line1", e.target.value)} placeholder="Elegance" testid="s-t1" />
              <Field label="Title · line 2 (italic)" value={editing.title_line2} onChange={(e) => set("title_line2", e.target.value)} placeholder="Redefined" testid="s-t2" />
              <Field label="Subtitle" value={editing.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Contemporary Indian Couture" testid="s-sub" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Button Text" value={editing.cta_text} onChange={(e) => set("cta_text", e.target.value)} testid="s-cta" />
                <Field label="Button Link" value={editing.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="/collections" testid="s-link" />
              </div>
              <Field label="Order" type="number" value={editing.order} onChange={(e) => set("order", e.target.value)} testid="s-order" />
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => set("is_active", e.target.checked)} data-testid="s-active" />
                Live on storefront
              </label>
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label className="overline">Slide Image</label>
                <button type="button" onClick={() => setShowPicker(true)} className="lux-link" data-testid="s-open-picker">From library →</button>
              </div>
              {editing.image ? (
                <div className="mt-4 aspect-[4/3] bg-muted overflow-hidden border border-border relative">
                  <img src={abs(editing.image)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/60" />
                  <div className="absolute bottom-4 left-4 right-4 text-ivory">
                    <p className="text-[10px] uppercase tracking-widest opacity-80">{editing.kicker}</p>
                    <p className="font-serif text-2xl md:text-3xl leading-tight mt-1">{editing.title_line1}{editing.title_line2 && <><br/><em className="not-italic">{editing.title_line2}</em></>}</p>
                  </div>
                </div>
              ) : (
                <div className="mt-4 aspect-[4/3] border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Pick an image from the library or paste a URL below.</div>
              )}
              <label className="overline text-muted-foreground mt-6 block">Image URL</label>
              <input value={editing.image} onChange={(e) => set("image", e.target.value)} placeholder="https://… or /api/media/{id}" className="lux-input" data-testid="s-image-url" />
            </div>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Media picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-ivory border border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-light tracking-tighter">Media Library</DialogTitle></DialogHeader>
          {media.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-6">Library empty. <Link to="/admin/media" className="underline">Upload files</Link> first.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-4">
              {media.filter(m => m.content_type?.startsWith("image/")).map(m => {
                const url = `/api/media/${m.id}`;
                const active = editing?.image === url || editing?.image === `${API}/media/${m.id}`;
                return (
                  <button key={m.id} type="button" onClick={() => { set("image", url); setShowPicker(false); }}
                    data-testid={`hp-${m.id}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${active ? "border-ink ring-2 ring-ink" : "border-border hover:border-ink"}`}>
                    <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {active && <span className="absolute top-2 right-2 w-6 h-6 bg-ink text-ivory flex items-center justify-center"><Check size={12} strokeWidth={1.5} /></span>}
                  </button>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input className="lux-input" data-testid={testid} {...rest} />
    </div>
  );
}
